/**
 * Tiny audio cue for button-tile hover on the dashboard.
 *
 * We synthesize a short "tick" with the Web Audio API rather than
 * shipping an mp3 — keeps the bundle small and the latency near
 * zero (no decode step). The sound is a damped sine pulse around
 * 1.2kHz, ~25ms total — feels like a soft physical button press
 * (the BlackBerry trackpad / scroll-wheel cue the user referenced).
 *
 * Browsers gate AudioContext until the user interacts with the
 * page, so the first hover may be silent. After any click anywhere
 * the audio is unlocked for the rest of the session.
 *
 * Usage:
 *   import { playHoverTick } from '../utils/uiSound';
 *   onMouseEnter={playHoverTick}
 *
 * Throttled internally (40ms) so a quick mouse swipe across many
 * tiles doesn't fire a chord of overlapping ticks.
 */

let _ctx = null;
let _lastPlay = 0;
let _audioUnlocked = false;
const THROTTLE_MS = 40;

// User-controlled mute, read from localStorage so it persists across
// reloads + tabs. Set via setSoundMuted() (the toggle next to the
// notification bell). Default is unmuted.
const MUTE_STORAGE_KEY = 'shadowland-sound-muted';
function isMuted() {
  try { return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1'; }
  catch { return false; }
}
export function setSoundMuted(muted) {
  try { window.localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0'); }
  catch { /* ignore quota / private mode */ }
}
export function getSoundMuted() {
  return isMuted();
}

// AudioContext can only be started inside a real user-gesture
// callback. Hover is NOT a "user gesture" for autoplay purposes, so
// both creation and resume stay inside click / keydown / pointerdown /
// touchstart handlers. Hover playback no-ops until this unlocks.
if (typeof window !== 'undefined') {
  const cleanupUnlockListeners = () => {
    window.removeEventListener('pointerdown', unlockAudio);
    window.removeEventListener('keydown', unlockAudio);
    window.removeEventListener('click', unlockAudio);
    window.removeEventListener('touchstart', unlockAudio);
  };
  const markUnlockedIfRunning = (ctx) => {
    if (ctx?.state === 'running') {
      _audioUnlocked = true;
      cleanupUnlockListeners();
    }
  };
  function unlockAudio() {
    if (isMuted()
        || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      return;
    }
    const ctx = ensureCtx();
    if (!ctx) {
      cleanupUnlockListeners();
      return;
    }
    if (ctx.state === 'running') {
      markUnlockedIfRunning(ctx);
      return;
    }
    if (ctx.state === 'suspended') {
      try {
        const resumed = ctx.resume();
        if (resumed && typeof resumed.then === 'function') {
          resumed.then(() => markUnlockedIfRunning(ctx)).catch(() => {});
        } else {
          markUnlockedIfRunning(ctx);
        }
      } catch {
        // Keep listeners installed so the next real gesture can retry.
      }
    }
  }
  window.addEventListener('pointerdown', unlockAudio);
  window.addEventListener('keydown', unlockAudio);
  window.addEventListener('click', unlockAudio);
  window.addEventListener('touchstart', unlockAudio);
}

function ensureCtx() {
  if (_ctx) return _ctx;
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try { _ctx = new Ctx(); } catch { return null; }
  return _ctx;
}

export function playHoverTick() {
  // User-controlled mute via the bell-row toggle.
  if (isMuted()) return;
  // Respect prefers-reduced-motion — users who opted out of motion
  // generally don't want surprise audio either.
  if (typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
  if (now - _lastPlay < THROTTLE_MS) return;
  _lastPlay = now;

  // Don't create or resume AudioContext from hover. Chrome treats
  // hover as autoplay and logs noisy warnings. The gesture listeners
  // above unlock audio; until then hovers stay silent.
  if (!_audioUnlocked) return;
  const ctx = ensureCtx();
  if (!ctx || ctx.state !== 'running') return;

  const start = ctx.currentTime;
  const dur = 0.025;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, start);
  // Slight downward sweep gives the tick a more "physical" feel
  // than a pure tone.
  osc.frequency.exponentialRampToValueAtTime(900, start + dur);

  const gain = ctx.createGain();
  // Quick attack, exponential decay — keeps the click brief without
  // a click-pop at the tail.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.18, start + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.005);
}
